// components/SelfPaced/CourseBuilderPage.js
// Matches VC_FE_REPLIT_UX_DESIGNS/SelfPacedCourseBuilder.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Spinner, Dropdown } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Edit, Trash2, MoreVertical, Users, Clock, IndianRupee, Globe, Layers,
  BookOpen, Eye, EyeOff, CheckCircle, PlayCircle,
} from 'lucide-react';
import {
  BackButton, AppButton, AppBadge, StatusBadge, StatsCard,
  EmptyState, ProgressBar, ConfirmModal, AlertModal,
} from '../ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import { AppCard, AppCardHeader, AppCardTitle, AppCardContent } from '../ui/AppCard';
import CommonToaster from '../Common/CommonToaster';
import CurriculumBuilder from './CurriculumBuilder';
import {
  getSelfPacedCourse,
  getCurriculum,
  getEnrollmentsByCourse,
  deleteSelfPacedCourse,
} from '../../utils/selfPacedApi';
import './_self-paced-pages.scss';

function formatPrice(price, currency) {
  if (!price || price === 0) return 'Free';
  if (currency === 'INR') return `₹${price.toLocaleString('en-IN')}`;
  if (currency === 'EUR') return `€${price}`;
  return `$${price}`;
}

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'N/A';

/* ─── Overview Tab ─────────────────────────────────────────── */
function OverviewTab({ course }) {
  const highlights = course.highlights
    ? course.highlights.split(',').map((h) => h.trim()).filter(Boolean)
    : [];

  return (
    <div className="sp-overview-grid">
      <div className="sp-overview-left">
        {/* Description */}
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>Description</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <p className="sp-overview-text">
              {course.description || 'No description available.'}
            </p>
          </AppCardContent>
        </AppCard>

        {/* Syllabus */}
        {course.syllabus && (
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>Syllabus Outline</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <p className="sp-overview-text">{course.syllabus}</p>
            </AppCardContent>
          </AppCard>
        )}

        {/* Prerequisites */}
        {course.prerequisites && (
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>Prerequisites</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <p className="sp-overview-text">{course.prerequisites}</p>
            </AppCardContent>
          </AppCard>
        )}

        {/* Learning Outcomes / Highlights */}
        {highlights.length > 0 && (
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>What You'll Learn</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="sp-learning-grid">
                {highlights.map((h, i) => (
                  <div key={i} className="sp-learning-item">
                    <CheckCircle />
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            </AppCardContent>
          </AppCard>
        )}
      </div>

      <div className="sp-overview-right">
        {/* Course Details */}
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>Course Details</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {[
              { label: 'Category', value: course.category || 'N/A' },
              { label: 'Level', value: course.level || 'N/A' },
              { label: 'Currency', value: course.currency || 'INR' },
              { label: 'Price', value: formatPrice(course.price, course.currency) },
              ...(course.discountPrice != null
                ? [{ label: 'Discount Price', value: formatPrice(course.discountPrice, course.currency) }]
                : []),
              {
                label: 'Visibility',
                value: (
                  <StatusBadge status={course.isPublished ? 'active' : 'inactive'}>
                    {course.isPublished ? 'Published' : 'Draft'}
                  </StatusBadge>
                ),
              },
              { label: 'Created', value: formatDate(course.createdAt) },
            ].map((row, i) => (
              <div key={i} className="sp-detail-row">
                <span className="sp-detail-row__label">{row.label}</span>
                <span className="sp-detail-row__value">{row.value}</span>
              </div>
            ))}
          </AppCardContent>
        </AppCard>
      </div>
    </div>
  );
}

/* ─── Enrollments Tab ──────────────────────────────────────── */
function EnrollmentsTab({ enrollments }) {
  if (enrollments.length === 0) {
    return (
      <AppCard>
        <AppCardContent style={{ padding: '2rem' }}>
          <EmptyState
            icon={<Users size={28} />}
            title="No enrollments yet"
            description="Students will appear here once they enroll in this course."
          />
        </AppCardContent>
      </AppCard>
    );
  }

  return (
    <AppCard>
      <AppCardContent style={{ padding: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="sp-enrollment-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Enrolled Date</th>
                <th>Progress</th>
                <th>Last Accessed</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((e) => (
                <tr key={e._id}>
                  <td>
                    <div className="sp-student-info">
                      <span className="sp-student-name">{e.student?.name || 'N/A'}</span>
                      <span className="sp-student-email">{e.student?.email || '—'}</span>
                    </div>
                  </td>
                  <td style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {formatDate(e.enrolledAt)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div className="sp-progress-bar">
                        <div
                          className="sp-progress-bar__fill"
                          style={{ width: `${e.completionPct || 0}%` }}
                        />
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        {e.completionPct || 0}%
                      </span>
                    </div>
                  </td>
                  <td style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {formatDate(e.lastAccessedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ─── Main Component ───────────────────────────────────────── */
const CourseBuilderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '' });

  const handleDeleteCourse = async () => {
    setDeleting(true);
    try {
      await deleteSelfPacedCourse(id);
      setToast({ show: true, message: 'Course deleted successfully.', type: 'success' });
      setTimeout(() => navigate('/self-paced'), 600);
    } catch (err) {
      if (err?.response?.data?.errorCode === 'COURSE_HAS_ENROLLMENTS') {
        setAlertModal({
          show: true,
          title: 'Cannot Delete Course',
          message: 'Course has active enrollments. Please refund/remove enrollments first.',
        });
      } else {
        setToast({
          show: true,
          message: err?.response?.data?.message || 'Failed to delete course.',
          type: 'error',
        });
      }
    } finally {
      setDeleting(false);
    }
  };

  const fetchCourse = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { data } = await getSelfPacedCourse(id);
      setCourse(data.course || data);
    } catch {
      setErr('Failed to load course details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchCourse(); }, [fetchCourse]);

  const fetchCurriculum = useCallback(async () => {
    try {
      const { data } = await getCurriculum(id);
      setCurriculum(data.data || []);
    } catch {
      setCurriculum([]);
    }
  }, [id]);

  const fetchEnrollments = useCallback(async () => {
    try {
      const { data } = await getEnrollmentsByCourse(id);
      setEnrollments(data.data || []);
    } catch {
      setEnrollments([]);
    }
  }, [id]);

  // Fetch curriculum + enrollments on mount
  useEffect(() => {
    fetchCurriculum();
    fetchEnrollments();
  }, [fetchCurriculum, fetchEnrollments]);

  if (loading) {
    return <div className="sp-loading"><Spinner animation="border" variant="secondary" /></div>;
  }

  if (err || !course) {
    return (
      <div className="sp-page">
        <AppButton variant="ghost" onClick={() => navigate('/self-paced')}>
          Back to Self-Paced Courses
        </AppButton>
        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <p style={{ fontSize: '1.125rem', fontWeight: 600 }}>{err || 'Course not found'}</p>
        </div>
      </div>
    );
  }

  const totalLessons = curriculum.reduce((sum, m) => sum + (m.lessons?.length || 0), 0);

  return (
    <div className="sp-page">
      {/* Header — matches REPLIT */}
      <div className="sp-builder-header">
        <div className="sp-builder-header__left">
          <BackButton onClick={() => navigate('/self-paced')} />
          <div className="sp-builder-header__info">
            <div className="sp-builder-header__title-row">
              <h1 className="sp-builder-header__title">{course.name}</h1>
              {course.category && <AppBadge variant="secondary">{course.category}</AppBadge>}
              <StatusBadge status={course.isPublished ? 'active' : 'inactive'}>
                {course.isPublished ? 'Published' : 'Draft'}
              </StatusBadge>
            </div>
            <p className="sp-builder-header__subtitle">
              {course.duration ? `${course.duration} total content` : 'Self-paced course'}
            </p>
          </div>
        </div>
        <div className="sp-builder-header__actions">
          <AppButton variant="outline" onClick={() => navigate(`/self-paced/edit/${id}`)}>
            <Edit size={16} /> Edit Course
          </AppButton>
          <Dropdown align="end">
            <Dropdown.Toggle as="button" className="app-btn app-btn--ghost app-btn--icon">
              <MoreVertical size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item className="text-danger" onClick={() => setShowDeleteConfirm(true)} disabled={deleting}>
                <Trash2 size={14} /> Delete Course
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Stats Grid — 6 columns matching REPLIT */}
      <div className="sp-stats-grid">
        <StatsCard title="Students" value={course.enrollmentCount || 0} icon={Users} iconColor="violet" />
        <StatsCard title="Modules" value={curriculum.length} icon={Layers} iconColor="emerald" />
        <StatsCard title="Lessons" value={totalLessons} icon={BookOpen} iconColor="amber" />
        <StatsCard title="Price" value={formatPrice(course.price, course.currency)} icon={IndianRupee} iconColor="rose" />
        <StatsCard title="Duration" value={course.duration || '—'} icon={Clock} iconColor="cyan" />
        <StatsCard title="Language" value={course.language || 'English'} icon={Globe} iconColor="blue" />
      </div>

      {/* Tabs — matches REPLIT */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="curriculum">Curriculum</TabsTrigger>
          <TabsTrigger value="enrollments">Enrollments</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab course={course} />
        </TabsContent>

        <TabsContent value="curriculum">
          <CurriculumBuilder
            courseId={id}
            course={course}
            modules={curriculum}
            onRefresh={fetchCurriculum}
            setToast={setToast}
          />
        </TabsContent>

        <TabsContent value="enrollments">
          <EnrollmentsTab enrollments={enrollments} />
        </TabsContent>
      </Tabs>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={() => { setShowDeleteConfirm(false); handleDeleteCourse(); }}
        title="Delete Course"
        message={`Delete "${course?.name}"? This will remove all modules, lessons, and enrollments. This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        loading={deleting}
      />

      <AlertModal
        show={alertModal.show}
        onClose={() => setAlertModal({ show: false, title: '', message: '' })}
        title={alertModal.title}
        message={alertModal.message}
        variant="danger"
      />
    </div>
  );
};

export default CourseBuilderPage;
